import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader
} from '../../styles/StyledComponents';
import { 
  FaCheckCircle, 
  FaTimes, 
  FaEye, 
  FaFileAlt,
  FaMoneyBillWave,
  FaBuilding,
  FaUser,
  FaCalendarAlt
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const ApprovalsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
`;

const ApprovalSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ApprovalCard = styled.div`
  background: white;
  border-radius: var(--border-radius);
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: var(--box-shadow);
  border-left: 4px solid var(--warning-color);
  
  &.approved {
    border-left-color: var(--success-color);
  }
  
  &.rejected {
    border-left-color: var(--danger-color);
  }
`;

const ApprovalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ApprovalTitle = styled.div`
  font-weight: 600;
  color: var(--secondary-color);
  font-size: 1.1rem;
`;

const ApprovalStatus = styled(Badge)`
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
`;

const ApprovalDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  .label {
    font-size: 0.8rem;
    color: var(--dark-gray);
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-weight: 600;
    color: var(--text-color);
  }
`;

const ApprovalActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionButton = styled(Button)<{ variant: 'approve' | 'reject' | 'view' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  
  ${props => props.variant === 'reject' && `
    background: var(--danger-color);
    &:hover {
      background: #d32f2f;
    }
  `}
  
  ${props => props.variant === 'view' && `
    background: var(--info-color);
    &:hover {
      background: #1976d2;
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const AdminApprovals: React.FC = () => {
  const { user } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState<any>({
    projects: [],
    payments: [],
    expenses: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog state
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const fetchPendingApprovals = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch pending projects (projects with pending budget approval)
        const projectsResponse = await apiService.getProjects();
        const allProjects = projectsResponse.data || [];
        const pendingProjects = allProjects.filter((project: any) => 
          project.status === 'Draft' || project.budgetStatus === 'Pending'
        );

        // Fetch pending payments
        const paymentsResponse = await apiService.getPayments();
        const allPayments = paymentsResponse.data || [];
        const pendingPayments = allPayments.filter((payment: any) => 
          payment.status === 'Pending' || !payment.approved
        );

        // Fetch pending expenses (expenses without receipts that need MD/TD approval)
        const expensesResponse = await apiService.getExpenses();
        const allExpenses = expensesResponse.data || [];
        const pendingExpenses = allExpenses.filter((expense: any) => 
          expense.status === 'Recorded' && !expense.receiptImage && !expense.approved
        );

        setPendingApprovals({
          projects: pendingProjects,
          payments: pendingPayments,
          expenses: pendingExpenses
        });

      } catch (error: any) {
        console.error('Error fetching pending approvals:', error);
        setError('Failed to load pending approvals');
      } finally {
        setLoading(false);
      }
    };

    fetchPendingApprovals();
  }, []);

  const handleApprove = async (type: string, id: string, action: 'approve' | 'reject', notes?: string) => {
    try {
      let response;
      
      switch (type) {
        case 'project':
          response = await apiService.approveProject(id, { action, comments: notes });
          break;
        case 'payment':
          response = await apiService.approvePayment(id, { approved: action === 'approve', notes });
          break;
        case 'expense':
          response = await apiService.approveExpense(id, { action, comments: notes });
          break;
        default:
          throw new Error('Invalid approval type');
      }

      if (response.success) {
        // Refresh the approvals list
        const updatedApprovals = { ...pendingApprovals };
        updatedApprovals[`${type}s`] = updatedApprovals[`${type}s`].filter((item: any) => item._id !== id);
        setPendingApprovals(updatedApprovals);
        
        showDialog('Success', `${type.charAt(0).toUpperCase() + type.slice(1)} ${action === 'approve' ? 'approved' : 'rejected'} successfully!`, 'success');
      }
    } catch (error: any) {
      console.error('Error approving item:', error);
      showDialog('Approval Failed', `Failed to ${action} ${type}: ${error.message}`, 'error');
    }
  };

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Pending': return 'warning';
      case 'Draft': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <ApprovalsContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading pending approvals...</p>
        </div>
      </ApprovalsContainer>
    );
  }

  if (error) {
    return (
      <ApprovalsContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </ApprovalsContainer>
    );
  }

  const totalPending = pendingApprovals.projects.length + pendingApprovals.payments.length + pendingApprovals.expenses.length;

  return (
    <ApprovalsContainer>
      <PageHeader>
        <PageTitle>
          <div className="title-icon">✅</div>
          Admin Approvals
        </PageTitle>
        <div>
          <Badge variant="warning">
            {totalPending} Pending Approvals
          </Badge>
        </div>
      </PageHeader>

      {totalPending === 0 ? (
        <EmptyState>
          <div className="empty-icon">✅</div>
          <h3>No Pending Approvals</h3>
          <p>All items have been approved or there are no items requiring approval.</p>
        </EmptyState>
      ) : (
        <>
          {/* Project Approvals */}
          {pendingApprovals.projects.length > 0 && (
            <ApprovalSection>
              <SectionTitle>
                <IconWrapper icon={FaBuilding} />
                Project Approvals ({pendingApprovals.projects.length})
              </SectionTitle>
              
              {pendingApprovals.projects.map((project: any) => (
                <ApprovalCard key={project._id}>
                  <ApprovalHeader>
                    <ApprovalTitle>{project.name}</ApprovalTitle>
                    <ApprovalStatus variant={getStatusVariant(project.status)}>
                      {project.status}
                    </ApprovalStatus>
                  </ApprovalHeader>
                  
                  <ApprovalDetails>
                    <DetailItem>
                      <div className="label">Project ID</div>
                      <div className="value">{project._id}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Budget</div>
                      <div className="value">{formatCurrency(project.budget?.total || 0)}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Location</div>
                      <div className="value">{project.location}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Created</div>
                      <div className="value">{formatDate(new Date(project.createdAt))}</div>
                    </DetailItem>
                  </ApprovalDetails>
                  
                  <ApprovalActions>
                    <ActionButton variant="view" as="a" href={`/projects/${project._id}`}>
                      <IconWrapper icon={FaEye} />
                      View Details
                    </ActionButton>
                    <ActionButton variant="approve" onClick={() => handleApprove('project', project._id, 'approve')}>
                      <IconWrapper icon={FaCheckCircle} />
                      Approve Project
                    </ActionButton>
                    <ActionButton variant="reject" onClick={() => handleApprove('project', project._id, 'reject')}>
                      <IconWrapper icon={FaTimes} />
                      Reject
                    </ActionButton>
                  </ApprovalActions>
                </ApprovalCard>
              ))}
            </ApprovalSection>
          )}

          {/* Payment Approvals */}
          {pendingApprovals.payments.length > 0 && (
            <ApprovalSection>
              <SectionTitle>
                <IconWrapper icon={FaMoneyBillWave} />
                Payment Approvals ({pendingApprovals.payments.length})
              </SectionTitle>
              
              {pendingApprovals.payments.map((payment: any) => (
                <ApprovalCard key={payment._id}>
                  <ApprovalHeader>
                    <ApprovalTitle>{payment.description}</ApprovalTitle>
                    <ApprovalStatus variant={getStatusVariant(payment.status)}>
                      {payment.status}
                    </ApprovalStatus>
                  </ApprovalHeader>
                  
                  <ApprovalDetails>
                    <DetailItem>
                      <div className="label">Payment ID</div>
                      <div className="value">{payment._id}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Amount</div>
                      <div className="value">{formatCurrency(payment.amount)}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Type</div>
                      <div className="value">{payment.paymentType}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Project</div>
                      <div className="value">{payment.projectName}</div>
                    </DetailItem>
                  </ApprovalDetails>
                  
                  <ApprovalActions>
                    <ActionButton variant="view" as="a" href={`/accounting/payment/${payment._id}`}>
                      <IconWrapper icon={FaEye} />
                      View Details
                    </ActionButton>
                    <ActionButton variant="approve" onClick={() => handleApprove('payment', payment._id, 'approve')}>
                      <IconWrapper icon={FaCheckCircle} />
                      Approve Payment
                    </ActionButton>
                    <ActionButton variant="reject" onClick={() => handleApprove('payment', payment._id, 'reject')}>
                      <IconWrapper icon={FaTimes} />
                      Reject
                    </ActionButton>
                  </ApprovalActions>
                </ApprovalCard>
              ))}
            </ApprovalSection>
          )}

          {/* Expense Approvals */}
          {pendingApprovals.expenses.length > 0 && (
            <ApprovalSection>
              <SectionTitle>
                <IconWrapper icon={FaFileAlt} />
                Expense Approvals ({pendingApprovals.expenses.length})
              </SectionTitle>
              
              {pendingApprovals.expenses.map((expense: any) => (
                <ApprovalCard key={expense._id}>
                  <ApprovalHeader>
                    <ApprovalTitle>{expense.description}</ApprovalTitle>
                    <ApprovalStatus variant={getStatusVariant(expense.status)}>
                      {expense.status}
                    </ApprovalStatus>
                  </ApprovalHeader>
                  
                  <ApprovalDetails>
                    <DetailItem>
                      <div className="label">Expense ID</div>
                      <div className="value">{expense._id}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Amount</div>
                      <div className="value">{formatCurrency(expense.amount)}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Category</div>
                      <div className="value">{expense.category}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Supplier</div>
                      <div className="value">{expense.supplier}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Receipt</div>
                      <div className="value">{expense.receiptImage ? 'Attached' : 'No Receipt'}</div>
                    </DetailItem>
                    <DetailItem>
                      <div className="label">Recorded By</div>
                      <div className="value">{expense.recordedBy?.name || 'Unknown'}</div>
                    </DetailItem>
                  </ApprovalDetails>
                  
                  <ApprovalActions>
                    <ActionButton variant="view" as="a" href={`/accounting/expense/${expense._id}`}>
                      <IconWrapper icon={FaEye} />
                      View Details
                    </ActionButton>
                    <ActionButton variant="approve" onClick={() => handleApprove('expense', expense._id, 'approve')}>
                      <IconWrapper icon={FaCheckCircle} />
                      Approve Expense
                    </ActionButton>
                    <ActionButton variant="reject" onClick={() => handleApprove('expense', expense._id, 'reject')}>
                      <IconWrapper icon={FaTimes} />
                      Reject
                    </ActionButton>
                  </ApprovalActions>
                </ApprovalCard>
              ))}
            </ApprovalSection>
          )}
        </>
      )}
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />
    </ApprovalsContainer>
  );
};

export default AdminApprovals; 